import React, { FC, useContext } from 'react';
import { Column, Row, FormInput } from 'Shared/components';
import styles from './styles.module.css';
import {
  CoverageOptionsResponse,
  CoveredPartyPayloadType,
  DriverType,
} from '@clearcover/core-app-types';
import {
  ModalLoading,
  ModalError,
  DollarSignIcon,
  Asset,
  CheckmarkIcon,
  CloseIcon,
} from '@clearcover/atlas';
import { useApi } from 'Shared/services/Api';
import { PolicyContext } from 'Policies/utils';
import { DriverReportTable } from './DriverReportTable';
import { useFormikContext, getIn } from 'formik';
import classNames from 'classnames';
import { DatePickerModal } from 'Shared/components/DatePickerModal';
import { formatDateShort, formatMoney, EMDASH } from 'Shared/utils';
import { STATE_OPTIONS } from 'Shared/constants/states';

const showIcon = (challenge?: string) => {
  return (
    <div className={styles.ratingIcon}>
      <Asset
        icon={challenge !== 'removed' ? CheckmarkIcon : CloseIcon}
        description={'Rated'}
        height={20}
        width={20}
        className={
          challenge !== 'removed' ? styles.checkmarkIcon : styles.closeIcon
        }
      />
    </div>
  );
};

type DriverReportsProps = {
  driver: DriverType;
  driverLicenseStatusOptions: Record<string, string>;
};

const optionalDate = (dateString: string) => {
  return dateString ? new Date(formatDateShort(dateString)) : undefined;
};

export const DriverReports: FC<DriverReportsProps> = ({
  driver: { accidents, violations, priorCarrier, driversLicense, sr22s, cfrs },
  driverLicenseStatusOptions,
}) => {
  const { details } = useContext(PolicyContext);
  const {
    handleBlur,
    handleChange,
    setFieldValue,
    values,
    touched,
    errors,
  } = useFormikContext<CoveredPartyPayloadType>();

  const [{ data: coverageOptions, loading, error }] = useApi<
    CoverageOptionsResponse
  >(`/options/coverages?identifier=${details.filingIdentifier}`);

  if (loading) {
    return <ModalLoading data-testid="coverage-modal-loading" />;
  }

  if (error) {
    return (
      <ModalError
        data-testid="edit-coverages-error"
        errorMessage={error?.message ?? ''}
        componentStack={error?.stack ?? ''}
      />
    );
  }

  const accidentsHasClassificationEnabled = !!accidents[0]?.classifications;

  const accidentsToShow = accidentsHasClassificationEnabled
    ? accidents.filter(
        ({ claimStatus, classifications }) =>
          claimStatus !== 'Collapsed' && !classifications?.lendingLoss
      )
    : accidents;

  // Drivers License error message
  let driversLicenseErrorMessage;

  if (values.info.licenseType === 'foreign') {
    driversLicenseErrorMessage =
      'Reports are not ordered for Foreign Driver’s Licences';
  } else if (values.info.licenseType === 'learners') {
    driversLicenseErrorMessage =
      'Reports are not ordered for Learner’s Permits';
  }

  return (
    <div className={styles.section} data-testid="driver-modal-attributes">
      <h1>Reports</h1>
      <p>
        We use third-party reports to verify driver accident and violation
        history
      </p>
      <Row
        data-testid="prior-insurance"
        className={styles.driverReportsSection}
      >
        <Column>
          <h3>Prior Insurance</h3>
          <DriverReportTable
            data-testid="prior-insurance-report-table"
            headers={[
              'RATED',
              'SOURCE',
              'CURRENT CARRIER',
              'DURATION',
              'BI LIMITS',
              '',
            ]}
            tableRows={[
              {
                data: [
                  <div
                    className={classNames(styles.reportData, {
                      [styles.small]: true,
                    })}
                  >
                    {showIcon(getIn(values, `reports.priorCarrier.challenge`))}
                  </div>,
                  <div
                    className={classNames(styles.reportData, {
                      [styles.small]: true,
                    })}
                  >
                    <span>{priorCarrier.source}</span>
                  </div>,
                  <div className={styles.reportData}>
                    <FormInput
                      variant="text"
                      name={'reports.priorCarrier.carrier'}
                      onChange={value => {
                        priorCarrier.source !== 'User Reported' &&
                          setFieldValue(
                            'reports.priorCarrier.challenge',
                            'edited'
                          );
                        handleChange(value);
                      }}
                      onBlur={handleBlur}
                      value={values.reports.priorCarrier.carrier}
                      errorMessage={
                        getIn(values, 'touched.reports.priorCarrier.carrier') &&
                        getIn(values, 'errors.reports.priorCarrier.carrier')
                      }
                      label="&nbsp;"
                      data-testid={'reports.priorCarrier.carrier'}
                    />
                  </div>,
                  <div className={styles.reportData}>
                    {values.reports.priorCarrier.isCurrentlyInsured ? (
                      <FormInput
                        variant="text"
                        label="currently insured months"
                        name={
                          'reports.priorCarrier.duration.currentlyInsuredMonths'
                        }
                        onChange={value => {
                          priorCarrier.source !== 'User Reported' &&
                            setFieldValue(
                              'reports.priorCarrier.challenge',
                              'edited'
                            );
                          handleChange(value);
                        }}
                        onBlur={handleBlur}
                        value={
                          values.reports.priorCarrier.duration
                            .currentlyInsuredMonths
                        }
                        errorMessage={
                          getIn(
                            values,
                            'touched.reports.priorCarrier.duration.currentlyInsuredMonths'
                          ) &&
                          getIn(
                            values,
                            'errors.reports.priorCarrier.duration.currentlyInsuredMonths'
                          )
                        }
                        data-testid={
                          'reports.priorCarrier.duration.currentlyInsuredMonths'
                        }
                      />
                    ) : (
                      <FormInput
                        variant="text"
                        label="days since last insured"
                        name={
                          'reports.priorCarrier.duration.daysSinceLastInsured'
                        }
                        onChange={value => {
                          priorCarrier.source !== 'User Reported' &&
                            setFieldValue(
                              'reports.priorCarrier.challenge',
                              'edited'
                            );
                          handleChange(value);
                        }}
                        onBlur={handleBlur}
                        errorMessage={
                          getIn(
                            values,
                            'touched.reports.priorCarrier.duration.daysSinceLastInsured'
                          ) &&
                          getIn(
                            values,
                            'errors.reports.priorCarrier.duration.daysSinceLastInsured'
                          )
                        }
                        value={
                          values.reports.priorCarrier.duration
                            .daysSinceLastInsured
                        }
                        data-testid={
                          'reports.priorCarrier.duration.daysSinceLastInsured'
                        }
                      />
                    )}
                  </div>,
                  <div className={styles.reportData}>
                    <FormInput
                      variant="select"
                      className={styles.dropdownMenu}
                      name={'reports.priorCarrier.bodyInjuryLimits'}
                      onChange={value => {
                        priorCarrier.source !== 'User Reported' &&
                          setFieldValue(
                            'reports.priorCarrier.challenge',
                            'edited'
                          );
                        setFieldValue(
                          'reports.priorCarrier.bodyInjuryLimits',
                          value
                        );
                      }}
                      value={values.reports.priorCarrier.bodyInjuryLimits}
                      label=""
                      data-testid={'reports.priorCarrier.bodyInjuryLimits'}
                      errorMessage={
                        getIn(
                          values,
                          'touched.reports.priorCarrier.bodyInjuryLimits'
                        ) &&
                        getIn(
                          values,
                          'errors.reports.priorCarrier.bodyInjuryLimits'
                        )
                      }
                      options={
                        coverageOptions.policy.coverages.find(
                          coverage => coverage.identifier === 'BI'
                        )?.coverageOptions ?? {}
                      }
                    />
                  </div>,
                ],
              },
            ]}
          />
        </Column>
      </Row>
      <Row
        data-testid="drivers-license"
        className={styles.driverReportsSection}
      >
        <Column>
          <h3>Driver’s License</h3>
          <DriverReportTable
            data-testid="drivers-license-report-table"
            headers={[
              'RATED',
              'SOURCE',
              'LICENSE NUMBER',
              'LICENSE STATE',
              'LICENSE STATUS',
              '',
            ]}
            tableRows={[
              {
                data: [
                  <div className={styles.reportData}>
                    {showIcon(
                      getIn(values, `reports.driversLicense.challenge`)
                    )}
                  </div>,
                  <div className={styles.reportData}>
                    <span>{driversLicense.source || '-'}</span>
                  </div>,
                  <div className={styles.reportData}>
                    <span>{driversLicense.number || '-'}</span>
                  </div>,
                  <div className={styles.reportData}>
                    <span>{driversLicense.state || '-'}</span>
                  </div>,
                  <div className={styles.reportData}>
                    {details.askForUserReportedLicenseStatus ? (
                      <span>
                        {driverLicenseStatusOptions?.[
                          values?.reports?.driversLicense?.reportStatus
                        ] || '-'}
                      </span>
                    ) : (
                      <FormInput
                        variant="select"
                        className={styles.dropdownMenu}
                        name={`reports.driversLicense.status`}
                        onChange={value => {
                          driversLicense.source !== 'User Reported' &&
                            setFieldValue(
                              'reports.driversLicense.challenge',
                              'edited'
                            );
                          setFieldValue('reports.driversLicense.status', value);
                        }}
                        value={values.reports.driversLicense.status}
                        label=""
                        data-testid={'drivers-license-status-input'}
                        options={driverLicenseStatusOptions}
                        errorMessage={
                          getIn(touched, 'reports.driversLicense.status') &&
                          getIn(errors, 'reports.driversLicense.status')
                        }
                        disabled={
                          getIn(values, 'reports.driversLicense.challenge') ===
                          'removed'
                        }
                      />
                    )}
                  </div>,
                ],
              },
            ]}
            errorMessage={driversLicenseErrorMessage}
          />
        </Column>
      </Row>
      {details?.showDriverCfrs ? (
        <Row
          data-testid="financial-responsibility"
          className={styles.driverReportsSection}
        >
          <Column>
            <h3>Certificate of Financial Responsibility (SR-22/FR-44)</h3>
            <DriverReportTable
              // eslint-disable-next-line testing-library/consistent-data-testid
              data-testid="cfr-reports-table"
              headers={[
                'RATED',
                'SOURCE',
                'CERTIFICATE',
                'CASE NUMBER',
                'STATE',
              ]}
              tableRows={cfrs.map(({ source, type, caseNumber, state }) => {
                const stateName =
                  STATE_OPTIONS?.[state as keyof typeof STATE_OPTIONS];
                return {
                  data: [
                    <span>{showIcon('CHECKED')}</span>, // "CHECKED" -> Always rendering "Checked" Icon
                    <span>{source}</span>,
                    <span>{type || 'None'}</span>,
                    <span>{caseNumber}</span>,
                    <span>{stateName || '-----'}</span>,
                  ],
                };
              })}
            />
          </Column>
        </Row>
      ) : (
        <Row
          // eslint-disable-next-line testing-library/consistent-data-testid
          data-testid="sr22s"
          className={styles.driverReportsSection}
        >
          <Column>
            <h3>SR-22</h3>
            {sr22s.length > 0 ? (
              <DriverReportTable
                // eslint-disable-next-line testing-library/consistent-data-testid
                data-testid="sr22s-report-table"
                headers={[
                  'RATED',
                  'SOURCE',
                  'HAS SR-22',
                  'START DATE',
                  'END DATE',
                  '',
                ]}
                tableRows={sr22s.map(sr22 => ({
                  formikChallengeLabel: `reports.sr22s.${sr22.id}.challenge`,
                  data: [
                    <div
                      className={classNames(styles.reportData, {
                        [styles.small]: true,
                      })}
                    >
                      {showIcon(
                        getIn(values, `reports.sr22s.${sr22.id}.challenge`)
                      )}
                    </div>,
                    <div
                      className={classNames(styles.reportData, {
                        [styles.small]: true,
                      })}
                    >
                      <span>{sr22.source}</span>
                    </div>,
                    <div
                      className={classNames(styles.reportData, {
                        [styles.small]: true,
                      })}
                    >
                      <span>{'Yes'}</span>
                    </div>,
                    <div className={styles.reportData}>
                      <div
                        className={classNames({
                          [styles.disabled]:
                            getIn(
                              values,
                              `reports.sr22s.${sr22.id}.challenge`
                            ) === 'removed',
                        })}
                      >
                        <DatePickerModal
                          showEffectiveTime={false}
                          label=""
                          startDate={
                            new Date(
                              formatDateShort(
                                getIn(
                                  values,
                                  `reports.sr22s.${sr22.id}.startDate`
                                ) ?? ''
                              )
                            )
                          }
                          errorMessage={
                            getIn(
                              touched,
                              `reports.sr22s.${sr22.id}.startDate`
                            ) &&
                            getIn(errors, `reports.sr22s.${sr22.id}.startDate`)
                          }
                          onDateChange={date => {
                            setFieldValue(
                              `reports.sr22s.${sr22.id}.challenge`,
                              'edited'
                            );
                            setFieldValue(
                              `reports.sr22s.${sr22.id}.startDate`,
                              date
                            );
                          }}
                        />
                      </div>
                    </div>,
                    <div
                      className={classNames({
                        [styles.disabled]:
                          getIn(
                            values,
                            `reports.sr22s.${sr22.id}.challenge`
                          ) === 'removed',
                      })}
                    >
                      <DatePickerModal
                        showEffectiveTime={false}
                        label=""
                        isOptional={true}
                        startDate={optionalDate(
                          getIn(values, `reports.sr22s.${sr22.id}.endDate`)
                        )}
                        errorMessage={
                          getIn(touched, `reports.sr22s.${sr22.id}.endDate`) &&
                          getIn(errors, `reports.sr22s.${sr22.id}.endDate`)
                        }
                        onDateChange={date => {
                          setFieldValue(
                            `reports.sr22s.${sr22.id}.challenge`,
                            'edited'
                          );
                          setFieldValue(
                            `reports.sr22s.${sr22.id}.endDate`,
                            date
                          );
                        }}
                      />
                    </div>,
                  ],
                }))}
              />
            ) : (
              <DriverReportTable
                // eslint-disable-next-line testing-library/consistent-data-testid
                data-testid="sr22s-report-table"
                headers={['RATED', 'SOURCE', 'HAS SR-22']}
                tableRows={[
                  {
                    data: [
                      <div
                        className={classNames(styles.reportData, {
                          [styles.small]: true,
                        })}
                      >
                        {showIcon('')}
                      </div>,
                      <div
                        className={classNames(styles.reportData, {
                          [styles.small]: true,
                        })}
                      >
                        <span>{EMDASH}</span>
                      </div>,
                      <div
                        className={classNames(styles.reportData, {
                          [styles.small]: true,
                        })}
                      >
                        <span>{'No'}</span>
                      </div>,
                    ],
                  },
                ]}
              />
            )}
          </Column>
        </Row>
      )}
      <Row data-testid="accidents" className={styles.driverReportsSection}>
        <Column>
          <h3>Accidents Found</h3>
          {accidentsToShow.length > 0 ? (
            <DriverReportTable
              data-testid="accidents-report-table"
              isClaimsList
              headers={
                accidentsHasClassificationEnabled
                  ? [
                      'Claim Status',
                      'Source',
                      'Accident Date',
                      'Type',
                      'Loss Amount',
                      'Fault',
                      '',
                    ]
                  : [
                      'CLAIM STATUS',
                      'SOURCE',
                      'ACCIDENT DATE',
                      'FAULT',
                      'DAMAGE AMOUNT',
                      '',
                    ]
              }
              tableRows={accidentsToShow.map(accident => {
                const edited = accident.challenge === 'edited';

                const isAtFaultInput = (
                  <div className={styles.reportData}>
                    <FormInput
                      variant="select"
                      className={styles.dropdownMenu}
                      name={`reports.accidents.${accident.id}.isAtFault`}
                      onChange={value => {
                        setFieldValue(
                          `reports.accidents.${accident.id}.challenge`,
                          'edited'
                        );
                        setFieldValue(
                          `reports.accidents.${accident.id}.isAtFault`,
                          value
                        );
                      }}
                      value={getIn(
                        values,
                        `reports.accidents.${accident.id}.isAtFault`
                      )}
                      label=""
                      data-testid={`accident-${accident.id}-fault-input`}
                      options={{ yes: 'Yes', no: 'No' }}
                      errorMessage={
                        getIn(
                          touched,
                          `reports.accidents.${accident.id}.isAtFault`
                        ) &&
                        getIn(
                          errors,
                          `reports.accidents.${accident.id}.isAtFault`
                        )
                      }
                      disabled={
                        edited ||
                        getIn(
                          values,
                          `reports.accidents.${accident.id}.challenge`
                        ) === 'removed'
                      }
                    />
                  </div>
                );
                return {
                  formikChallengeLabel: edited
                    ? undefined
                    : `reports.accidents.${accident.id}.challenge`,
                  data: [
                    <div
                      className={classNames(styles.reportData, {
                        [styles.small]: true,
                      })}
                    >
                      {accident.claimStatus}
                    </div>,
                    <div
                      className={classNames(styles.reportData, {
                        [styles.small]: true,
                      })}
                    >
                      <span>{accident.source}</span>
                    </div>,
                    <div className={styles.reportData}>
                      <div
                        className={classNames({
                          [styles.disabled]:
                            getIn(
                              values,
                              `reports.accidents.${accident.id}.challenge`
                            ) === 'removed',
                        })}
                      >
                        <DatePickerModal
                          showEffectiveTime={false}
                          label=""
                          startDate={
                            new Date(
                              formatDateShort(
                                getIn(
                                  values,
                                  `reports.accidents.${accident.id}.accidentDate`
                                ) ?? ''
                              )
                            )
                          }
                          errorMessage={
                            getIn(
                              touched,
                              `reports.accidents.${accident.id}.accidentDate`
                            ) &&
                            getIn(
                              errors,
                              `reports.accidents.${accident.id}.accidentDate`
                            )
                          }
                          onDateChange={date => {
                            setFieldValue(
                              `reports.accidents.${accident.id}.challenge`,
                              'edited'
                            );
                            setFieldValue(
                              `reports.accidents.${accident.id}.accidentDate`,
                              date
                            );
                          }}
                          disabled={edited}
                        />
                      </div>
                    </div>,
                    accidentsHasClassificationEnabled ? (
                      <div className={styles.reportData}>
                        <FormInput
                          variant="select"
                          className={styles.dropdownMenu}
                          name={`reports.accidents.${accident.id}.classifications.claimClassificationTypeV2`}
                          onChange={value => {
                            setFieldValue(
                              `reports.accidents.${accident.id}.challenge`,
                              'edited'
                            );
                            setFieldValue(
                              `reports.accidents.${accident.id}.classifications.claimClassificationTypeV2`,
                              value
                            );
                          }}
                          value={getIn(
                            values,
                            `reports.accidents.${accident.id}.classifications.claimClassificationTypeV2`
                          )}
                          label=""
                          data-testid={`accident-${accident.id}-claim-type-input`}
                          options={{
                            'RA Only': 'RA Only',
                            'COMP Only': 'COMP Only',
                            'GLS Only': 'Glass Only',
                            'At Fault': 'At-Fault Accident',
                            'Not At Fault': 'Not At-Fault Accident',
                          }}
                          errorMessage={
                            getIn(
                              touched,
                              `reports.accidents.${accident.id}.classifications.claimClassificationTypeV2`
                            ) &&
                            getIn(
                              errors,
                              `reports.accidents.${accident.id}.classifications.claimClassificationTypeV2`
                            )
                          }
                          disabled={
                            edited ||
                            getIn(
                              values,
                              `reports.accidents.${accident.id}.challenge`
                            ) === 'removed'
                          }
                        />
                      </div>
                    ) : (
                      isAtFaultInput
                    ),
                    <div className={styles.reportData}>
                      <FormInput
                        variant="text"
                        icon={
                          <Asset
                            icon={DollarSignIcon}
                            description="dollar sign"
                            height={20}
                            width={20}
                          />
                        }
                        label=""
                        name={`reports.accidents.${accident.id}.damageAmount`}
                        onChange={value => {
                          setFieldValue(
                            `reports.accidents.${accident.id}.challenge`,
                            'edited'
                          );
                          handleChange(value);
                        }}
                        onBlur={() => {
                          const value = getIn(
                            values,
                            `reports.accidents.${accident.id}.damageAmount`
                          );
                          const money = value.length
                            ? formatMoney(value, {
                                includeCents: true,
                                formatWithSymbol: false,
                              })
                            : '';

                          setFieldValue(
                            `reports.accidents.${accident.id}.damageAmount`,
                            money
                          );
                        }}
                        value={getIn(
                          values,
                          `reports.accidents.${accident.id}.damageAmount`
                        )}
                        errorMessage={
                          getIn(
                            touched,
                            `reports.accidents.${accident.id}.damageAmount`
                          ) &&
                          getIn(
                            errors,
                            `reports.accidents.${accident.id}.damageAmount`
                          )
                        }
                        data-testid={`reports.accidents.${accident.id}.damageAmount`}
                        disabled={
                          edited ||
                          getIn(
                            values,
                            `reports.accidents.${accident.id}.challenge`
                          ) === 'removed'
                        }
                      />
                    </div>,
                    accidentsHasClassificationEnabled ? isAtFaultInput : <></>,
                  ],
                };
              })}
            />
          ) : (
            <p>No accidents found</p>
          )}
        </Column>
      </Row>
      <Row data-testid="violations" className={styles.driverReportsSection}>
        <Column>
          <h3>Violations Found</h3>
          {violations.length > 0 ? (
            <DriverReportTable
              data-testid="violations-report-table"
              headers={[
                'RATED',
                'SOURCE',
                'VIOLATION DATE',
                'CONVICTION DATE',
                'DESCRIPTION',
                '',
              ]}
              tableRows={violations.map(violation => {
                return {
                  formikChallengeLabel: `reports.violations.${violation.id}.challenge`,
                  data: [
                    <div
                      className={classNames(styles.reportData, {
                        [styles.small]: true,
                      })}
                    >
                      {showIcon(
                        getIn(
                          values,
                          `reports.violations.${violation.id}.challenge`
                        )
                      )}
                    </div>,
                    <div
                      className={classNames(styles.reportData, {
                        [styles.small]: true,
                      })}
                    >
                      <span>{violation.source}</span>
                    </div>,
                    <div className={styles.reportData}>
                      <div
                        className={classNames({
                          [styles.disabled]:
                            getIn(
                              values,
                              `reports.violations.${violation.id}.challenge`
                            ) === 'removed',
                        })}
                      >
                        <DatePickerModal
                          showEffectiveTime={false}
                          label=""
                          startDate={
                            new Date(
                              formatDateShort(
                                getIn(
                                  values,
                                  `reports.violations.${violation.id}.violationDate`
                                ) ?? ''
                              )
                            )
                          }
                          errorMessage={
                            getIn(
                              touched,
                              `reports.violations.${violation.id}.violationDate`
                            ) &&
                            getIn(
                              errors,
                              `reports.violations.${violation.id}.violationDate`
                            )
                          }
                          onDateChange={date => {
                            setFieldValue(
                              `reports.violations.${violation.id}.challenge`,
                              'edited'
                            );
                            setFieldValue(
                              `reports.violations.${violation.id}.violationDate`,
                              date
                            );
                          }}
                        />
                      </div>
                    </div>,
                    <div className={styles.reportData}>
                      <div
                        className={classNames({
                          [styles.disabled]:
                            getIn(
                              values,
                              `reports.violations.${violation.id}.challenge`
                            ) === 'removed',
                        })}
                      >
                        <DatePickerModal
                          showEffectiveTime={false}
                          label=""
                          isOptional={true}
                          startDate={optionalDate(
                            getIn(
                              values,
                              `reports.violations.${violation.id}.convictionDate`
                            )
                          )}
                          errorMessage={
                            getIn(
                              touched,
                              `reports.violations.${violation.id}.convictionDate`
                            ) &&
                            getIn(
                              errors,
                              `reports.violations.${violation.id}.convictionDate`
                            )
                          }
                          onDateChange={date => {
                            setFieldValue(
                              `reports.violations.${violation.id}.challenge`,
                              'edited'
                            );
                            setFieldValue(
                              `reports.violations.${violation.id}.convictionDate`,
                              date
                            );
                          }}
                        />
                      </div>
                    </div>,
                    <div className={styles.reportData}>
                      <FormInput
                        variant="select"
                        className={styles.dropdownMenu}
                        name={`reports.violations.${violation.id}.description`}
                        onChange={value => {
                          setFieldValue(
                            `reports.violations.${violation.id}.challenge`,
                            'edited'
                          );
                          setFieldValue(
                            `reports.violations.${violation.id}.description`,
                            value
                          );
                        }}
                        value={getIn(
                          values,
                          `reports.violations.${violation.id}.description`
                        )}
                        label=""
                        data-testid={`violation-description-${violation.id}-input`}
                        options={violation.descriptionOptions}
                        errorMessage={
                          getIn(
                            touched,
                            `reports.violations.${violation.id}.description`
                          ) &&
                          getIn(
                            errors,
                            `reports.violations.${violation.id}.description`
                          )
                        }
                        disabled={
                          getIn(
                            values,
                            `reports.violations.${violation.id}.challenge`
                          ) === 'removed'
                        }
                      />
                    </div>,
                  ],
                };
              })}
            />
          ) : (
            <p>No violations found</p>
          )}
        </Column>
      </Row>
    </div>
  );
};
